import {
    ActionIcon,
    Box,
    Button,
    Checkbox,
    ColorInput,
    Divider,
    Group,
    NumberInput,
    Paper,
    Stack,
    Tabs,
    Text,
    Textarea,
    TextInput,
    Title,
} from '@mantine/core';
import {DateInput} from '@mantine/dates';
import {useForm} from '@mantine/form';
import {closeModal, openModal} from '@mantine/modals';
import {notifications} from '@mantine/notifications';
import {IconGripVertical, IconPencil, IconPlus, IconTrash, IconUpload} from '@tabler/icons-react';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import dayjs from 'dayjs';
import {useEffect, useMemo, useRef, useState} from 'react';
import {
    SettingsService,
    type LocalizedText,
    type MediaArchiveLink,
    type MediaArchiveLinkAdd,
    type MediaArchiveYear,
    type SiteSettings,
    type WhatToExpectSection,
    type WhatToExpectSectionAdd,
    type WhatToExpectSectionSummary,
} from '../../client';
import {formatRequestError} from '../../utils/formatRequestError';
import {DeleteTableAction} from '../common/TableActionIcons';
import {PageLayout} from '../shell/PageLayout';
import {TablerIconImage} from '../common/TablerIconImage';
import {TablerIconPicker} from './TablerIconPicker';
import {getSiteSettingsFormErrors, isSiteSettingsFormValid} from './siteSettingsForm';

function parseDate(value: string): Date | null {
    if (!value) {
        return null;
    }
    const parsed = dayjs(value, 'YYYY-MM-DD', true);
    return parsed.isValid() ? parsed.toDate() : null;
}

function dateFieldValue(value: string | null): string {
    return value ?? '';
}

function yearTabLabel(year: MediaArchiveYear): string {
    return year.subtitle ? `${year.year} / ${year.subtitle}` : String(year.year);
}

const EMPTY_LOCALIZED: LocalizedText = {de: '', en: '', fr: ''};

function sectionPreviewTitle(section: Pick<WhatToExpectSectionSummary, 'id' | 'title'>): string {
    return section.title.de || section.title.en || section.title.fr || `Section ${section.id}`;
}

function cloneSiteSettings(settings: SiteSettings): SiteSettings {
    return {
        ...settings,
        tshirts_woman: [...settings.tshirts_woman],
        tshirts_men: [...settings.tshirts_men],
    };
}

function DocumentLogoSection({
    originalName,
}: {
    originalName: string;
}) {
    const queryClient = useQueryClient();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [uploading, setUploading] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const hasLogo = Boolean(originalName.trim());

    useEffect(() => {
        let cancelled = false;
        let objectUrl: string | null = null;

        const loadPreview = async () => {
            if (!hasLogo) {
                setPreviewUrl((prev) => {
                    if (prev) {
                        URL.revokeObjectURL(prev);
                    }
                    return null;
                });
                return;
            }
            try {
                const blob = await SettingsService.getDocumentLogoBlob();
                if (cancelled) {
                    return;
                }
                objectUrl = URL.createObjectURL(blob);
                setPreviewUrl((prev) => {
                    if (prev) {
                        URL.revokeObjectURL(prev);
                    }
                    return objectUrl;
                });
            } catch {
                if (!cancelled) {
                    setPreviewUrl((prev) => {
                        if (prev) {
                            URL.revokeObjectURL(prev);
                        }
                        return null;
                    });
                }
            }
        };

        void loadPreview();
        return () => {
            cancelled = true;
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [hasLogo, originalName]);

    const invalidateSettings = () => {
        void queryClient.invalidateQueries({queryKey: ['site-settings']});
    };

    const handleFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) {
            return;
        }
        setUploading(true);
        SettingsService.uploadDocumentLogoApiV1SettingsDocumentLogoPost({
            formData: {file},
        })
            .then(() => {
                invalidateSettings();
                notifications.show({
                    color: 'green',
                    title: 'Logo uploaded',
                    message: 'Document PDFs will use this logo after Generate PDFs.',
                });
            })
            .catch((error) => {
                notifications.show({
                    color: 'red',
                    title: 'Could not upload logo',
                    message: formatRequestError(error, 'Request failed'),
                });
            })
            .finally(() => setUploading(false));
    };

    const handleDelete = () => {
        setDeleting(true);
        SettingsService.deleteDocumentLogoApiV1SettingsDocumentLogoDelete()
            .then(() => {
                invalidateSettings();
                notifications.show({
                    color: 'green',
                    title: 'Logo removed',
                    message: 'Document PDFs will no longer include a logo.',
                });
            })
            .catch((error) => {
                notifications.show({
                    color: 'red',
                    title: 'Could not remove logo',
                    message: formatRequestError(error, 'Request failed'),
                });
            })
            .finally(() => setDeleting(false));
    };

    return (
        <Stack gap="sm">
            <Title order={4}>Document logo</Title>
            <Text size="sm" c="dimmed">
                Optional SVG used on generated QR and sign PDFs, centered under the text.
            </Text>
            {hasLogo ? (
                <Group align="flex-start" gap="md">
                    <Paper withBorder p="sm" radius="md">
                        {previewUrl ? (
                            <img
                                src={previewUrl}
                                alt="Document logo preview"
                                style={{maxWidth: 200, maxHeight: 120, display: 'block'}}
                            />
                        ) : (
                            <Text size="sm" c="dimmed">Loading preview…</Text>
                        )}
                    </Paper>
                    <Stack gap={4}>
                        <Text size="sm">{originalName}</Text>
                        <Group gap="xs">
                            <Button
                                variant="default"
                                leftSection={<IconUpload size={16}/>}
                                loading={uploading}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                Replace SVG
                            </Button>
                            <Button
                                variant="light"
                                color="red"
                                leftSection={<IconTrash size={16}/>}
                                loading={deleting}
                                onClick={handleDelete}
                            >
                                Remove
                            </Button>
                        </Group>
                    </Stack>
                </Group>
            ) : (
                <Button
                    variant="default"
                    leftSection={<IconUpload size={16}/>}
                    loading={uploading}
                    onClick={() => fileInputRef.current?.click()}
                    w="fit-content"
                >
                    Upload SVG logo
                </Button>
            )}
            <input
                ref={fileInputRef}
                type="file"
                accept=".svg,image/svg+xml"
                style={{display: 'none'}}
                onChange={handleFileSelected}
            />
        </Stack>
    );
}

function SettingsForm() {
    const {data, isLoading, isError} = useQuery({
        queryKey: ['site-settings'],
        queryFn: SettingsService.getSiteSettingsApiV1SettingsGet,
    });

    if (isLoading) {
        return <Text c="dimmed">Loading settings…</Text>;
    }

    if (!data) {
        return (
            <Text c="dimmed">
                {isError ? 'Could not load settings.' : 'Loading settings…'}
            </Text>
        );
    }

    return <SettingsFormFields settings={cloneSiteSettings(data)}/>;
}

function SettingsFormFields({settings}: {settings: SiteSettings}) {
    const queryClient = useQueryClient();

    const form = useForm<SiteSettings>({
        initialValues: settings,
        validate: (values) => getSiteSettingsFormErrors(values),
        validateInputOnBlur: true,
    });

    useEffect(() => {
        form.initialize(cloneSiteSettings(settings));
    }, [settings]);

    const saveMutation = useMutation({
        mutationFn: (values: SiteSettings) =>
            SettingsService.updateSiteSettingsApiV1SettingsPut({requestBody: values}),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['site-settings']});
            notifications.show({
                color: 'green',
                title: 'Settings saved',
                message: 'Site settings were updated successfully.',
            });
        },
        onError: (error: {body?: {detail?: string}; message?: string}) => {
            notifications.show({
                color: 'red',
                title: 'Could not save settings',
                message: error.body?.detail ?? error.message ?? 'Request failed',
            });
        },
    });

    const setSizeList = (field: 'tshirts_woman' | 'tshirts_men', raw: string) => {
        form.setFieldValue(
            field,
            raw.split(',').map((entry) => entry.trim()).filter(Boolean),
        );
        form.validateField('tshirts_woman');
        form.validateField('tshirts_men');
    };

    const validateTshirtFields = () => {
        form.validateField('tshirts_woman');
        form.validateField('tshirts_men');
    };

    const canSave = isSiteSettingsFormValid(form.values);

    return (
        <form
            onSubmit={form.onSubmit(
                (values) => saveMutation.mutate(values),
                () => {
                    notifications.show({
                        color: 'red',
                        title: 'Cannot save settings',
                        message: 'Please fill in all required fields.',
                    });
                },
            )}
        >
            <Stack gap="lg">
                <Stack gap="sm">
                    <Title order={4}>Event</Title>
                    <Group grow align="flex-start">
                        <NumberInput label="Event count" min={1} required {...form.getInputProps('eventcount')}/>
                        <NumberInput label="Max registrations" min={1} required {...form.getInputProps('registration_max')}/>
                        <NumberInput label="Registration fee" min={0} decimalScale={2} required {...form.getInputProps('registration_fee')}/>
                        <NumberInput label="Payment reminder days" min={1} required {...form.getInputProps('payment_reminder_days')}/>
                    </Group>
                    <Group grow align="flex-start">
                        <DateInput
                            label="Start date"
                            required
                            valueFormat="YYYY-MM-DD"
                            value={parseDate(form.values.date_start)}
                            error={form.errors.date_start}
                            onChange={(value) => form.setFieldValue('date_start', dateFieldValue(value))}
                            onBlur={() => form.validateField('date_start')}
                        />
                        <DateInput
                            label="End date"
                            required
                            valueFormat="YYYY-MM-DD"
                            value={parseDate(form.values.date_end)}
                            error={form.errors.date_end}
                            onChange={(value) => form.setFieldValue('date_end', dateFieldValue(value))}
                            onBlur={() => form.validateField('date_end')}
                        />
                    </Group>
                    <Group grow align="flex-start">
                        <DateInput
                            label="Last registration date"
                            required
                            valueFormat="YYYY-MM-DD"
                            value={parseDate(form.values.registration_last_register)}
                            error={form.errors.registration_last_register}
                            onChange={(value) => form.setFieldValue('registration_last_register', dateFieldValue(value))}
                            onBlur={() => form.validateField('registration_last_register')}
                        />
                        <DateInput
                            label="Last cancel date"
                            required
                            valueFormat="YYYY-MM-DD"
                            value={parseDate(form.values.registration_last_cancel)}
                            error={form.errors.registration_last_cancel}
                            onChange={(value) => form.setFieldValue('registration_last_cancel', dateFieldValue(value))}
                            onBlur={() => form.validateField('registration_last_cancel')}
                        />
                    </Group>
                    <Checkbox
                        label="Show registration status on registration page"
                        {...form.getInputProps('show_registration_status', {type: 'checkbox'})}
                    />
                </Stack>

                <Divider/>

                <Stack gap="sm">
                    <Title order={4}>Homepage</Title>
                    <TextInput label="Video URL" required {...form.getInputProps('video_url')}/>
                </Stack>

                <Divider/>

                <Stack gap="sm">
                    <Title order={4}>Branding</Title>
                    <ColorInput label="Accent color" required {...form.getInputProps('color')}/>
                </Stack>

                <Divider/>

                <Stack gap="sm">
                    <Title order={4}>Maintenance</Title>
                    <Checkbox label="Maintenance mode active" {...form.getInputProps('maintenance_active', {type: 'checkbox'})}/>
                    <Checkbox
                        label="Show camp date on homepage"
                        {...form.getInputProps('maintenance_show_date', {type: 'checkbox'})}
                        disabled={!form.values.maintenance_active}
                    />
                    <Textarea
                        label="Maintenance text"
                        minRows={3}
                        required={form.values.maintenance_active}
                        {...form.getInputProps('maintenance_text')}
                        disabled={!form.values.maintenance_active}
                    />
                </Stack>

                <Divider/>

                <Stack gap="sm">
                    <Title order={4}>Domain and contact</Title>
                    <Group grow align="flex-start">
                        <TextInput label="Domain" required {...form.getInputProps('domain')}/>
                        <TextInput label="Contact email" required {...form.getInputProps('mail')}/>
                        <TextInput label="Sani team phone" required {...form.getInputProps('saniphone')}/>
                    </Group>
                    <Group grow align="flex-start">
                        <TextInput label="Venue name" required {...form.getInputProps('address_name')}/>
                        <TextInput label="Street" required {...form.getInputProps('address_street')}/>
                    </Group>
                    <Group grow align="flex-start">
                        <TextInput label="City" required {...form.getInputProps('address_city')}/>
                        <TextInput label="ZIP code" required {...form.getInputProps('address_zipcode')}/>
                    </Group>
                    <TextInput label="Maps link" required {...form.getInputProps('address_maps_link')}/>
                    <Group grow align="flex-start">
                        <TextInput label="Parking label" required {...form.getInputProps('parking_label')}/>
                        <TextInput label="Parking URL" required {...form.getInputProps('parking_url')}/>
                    </Group>
                </Stack>

                <Divider/>

                <Stack gap="sm">
                    <Title order={4}>Bank details</Title>
                    <Text size="sm" c="dimmed">
                        Used for the Kontodaten QR document. Account holder and IBAN are
                        required when generating PDFs. The QR amount uses the registration fee.
                    </Text>
                    <Group grow align="flex-start">
                        <TextInput label="Account holder" {...form.getInputProps('bank_account_holder')}/>
                        <TextInput label="IBAN" {...form.getInputProps('bank_iban')}/>
                    </Group>
                    <TextInput label="Remittance (optional)" {...form.getInputProps('bank_remittance')}/>
                </Stack>

                <Divider/>

                <DocumentLogoSection
                    originalName={form.values.document_logo_original_name || ''}
                />

                <Divider/>

                <Stack gap="sm">
                    <Title order={4}>T-shirt sizes</Title>
                    <Text size="sm" c="dimmed">
                        Provide at least one list — women&apos;s or men&apos;s sizes.
                    </Text>
                    <TextInput
                        label="Women sizes (comma-separated)"
                        error={form.errors.tshirts_woman}
                        value={form.values.tshirts_woman.join(', ')}
                        onChange={(event) => setSizeList('tshirts_woman', event.currentTarget.value)}
                        onBlur={validateTshirtFields}
                    />
                    <TextInput
                        label="Men sizes (comma-separated)"
                        error={form.errors.tshirts_men}
                        value={form.values.tshirts_men.join(', ')}
                        onChange={(event) => setSizeList('tshirts_men', event.currentTarget.value)}
                        onBlur={validateTshirtFields}
                    />
                </Stack>

                <Group justify="flex-end">
                    <Button type="submit" loading={saveMutation.isPending} disabled={!canSave}>
                        Save settings
                    </Button>
                </Group>
            </Stack>
        </form>
    );
}

function DraggableLinkRow({
    link,
    index,
    onReorder,
    onEdit,
    onDelete,
}: {
    link: MediaArchiveLink;
    index: number;
    onReorder: (from: number, to: number) => void;
    onEdit: () => void;
    onDelete: () => void;
}) {
    return (
        <Paper
            withBorder
            p="sm"
            draggable
            onDragStart={(event) => {
                event.dataTransfer.setData('text/plain', String(index));
                event.dataTransfer.effectAllowed = 'move';
            }}
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => {
                event.preventDefault();
                const from = Number(event.dataTransfer.getData('text/plain'));
                if (!Number.isNaN(from) && from !== index) {
                    onReorder(from, index);
                }
            }}
        >
            <Group wrap="nowrap" align="flex-start">
                <ActionIcon variant="subtle" aria-label="Drag to reorder">
                    <IconGripVertical size={16}/>
                </ActionIcon>
                <Stack gap={2} style={{flex: 1, minWidth: 0}}>
                    <Text fw={500}>{link.title}</Text>
                    <Text size="sm" c="dimmed" truncate>{link.url}</Text>
                </Stack>
                <ActionIcon variant="subtle" aria-label="Edit link" onClick={onEdit}>
                    <IconPencil size={16}/>
                </ActionIcon>
                <DeleteTableAction onClick={onDelete}/>
            </Group>
        </Paper>
    );
}

function LinkForm({
    initial,
    onSubmit,
}: {
    initial: MediaArchiveLinkAdd;
    onSubmit: (values: MediaArchiveLinkAdd) => void;
}) {
    const form = useForm<MediaArchiveLinkAdd>({initialValues: initial});
    return (
        <form onSubmit={form.onSubmit(onSubmit)}>
            <Stack>
                <TextInput label="Title" required {...form.getInputProps('title')}/>
                <TextInput label="URL" required {...form.getInputProps('url')}/>
                <Group justify="flex-end">
                    <Button type="submit">Save</Button>
                </Group>
            </Stack>
        </form>
    );
}

function YearForm({
    initial,
    onSubmit,
}: {
    initial: {year: number; subtitle: string};
    onSubmit: (values: {year: number; subtitle: string}) => void;
}) {
    const form = useForm({initialValues: initial});
    return (
        <form onSubmit={form.onSubmit(onSubmit)}>
            <Stack>
                <NumberInput label="Year" min={1900} max={9999} {...form.getInputProps('year')}/>
                <TextInput label="Subtitle" placeholder="Camp X" {...form.getInputProps('subtitle')}/>
                <Group justify="flex-end">
                    <Button type="submit">Save</Button>
                </Group>
            </Stack>
        </form>
    );
}

function WhatToExpectSectionForm({
    initial,
    onSubmit,
}: {
    initial: WhatToExpectSectionAdd;
    onSubmit: (values: WhatToExpectSectionAdd) => void;
}) {
    const form = useForm<WhatToExpectSectionAdd>({
        initialValues: initial,
    });
    return (
        <form onSubmit={form.onSubmit(onSubmit)}>
            <Stack>
                <TablerIconPicker
                    value={form.values.icon}
                    onChange={(icon) => form.setFieldValue('icon', icon)}
                />
                <Text fw={500}>Titles</Text>
                <TextInput label="Title (German)" required {...form.getInputProps('title.de')}/>
                <TextInput label="Title (English)" required {...form.getInputProps('title.en')}/>
                <TextInput label="Title (French)" required {...form.getInputProps('title.fr')}/>
                <Text fw={500}>Text</Text>
                <Textarea label="Text (German)" minRows={3} required {...form.getInputProps('text.de')}/>
                <Textarea label="Text (English)" minRows={3} required {...form.getInputProps('text.en')}/>
                <Textarea label="Text (French)" minRows={3} required {...form.getInputProps('text.fr')}/>
                <Group justify="flex-end">
                    <Button type="submit">Save</Button>
                </Group>
            </Stack>
        </form>
    );
}

function DraggableSectionRow({
    section,
    index,
    onReorder,
    onEdit,
    onDelete,
}: {
    section: WhatToExpectSectionSummary;
    index: number;
    onReorder: (from: number, to: number) => void;
    onEdit: () => void;
    onDelete: () => void;
}) {
    return (
        <Paper
            withBorder
            p="sm"
            draggable
            onDragStart={(event) => {
                event.dataTransfer.setData('text/plain', String(index));
                event.dataTransfer.effectAllowed = 'move';
            }}
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => {
                event.preventDefault();
                const from = Number(event.dataTransfer.getData('text/plain'));
                if (!Number.isNaN(from) && from !== index) {
                    onReorder(from, index);
                }
            }}
        >
            <Group wrap="nowrap" align="flex-start">
                <ActionIcon variant="subtle" aria-label="Drag to reorder">
                    <IconGripVertical size={16}/>
                </ActionIcon>
                <TablerIconImage icon={section.icon} size={20} style={{flexShrink: 0}}/>
                <Stack gap={2} style={{flex: 1, minWidth: 0}}>
                    <Text fw={500}>{sectionPreviewTitle(section)}</Text>
                    <Text size="sm" c="dimmed" truncate>
                        {[section.title.de, section.title.en, section.title.fr]
                            .filter(Boolean)
                            .join(' · ')}
                    </Text>
                </Stack>
                <ActionIcon variant="subtle" aria-label="Edit section" onClick={onEdit}>
                    <IconPencil size={16}/>
                </ActionIcon>
                <DeleteTableAction onClick={onDelete}/>
            </Group>
        </Paper>
    );
}

function WhatToExpectTab() {
    const queryClient = useQueryClient();
    const {data, isLoading} = useQuery({
        queryKey: ['what-to-expect-overview'],
        queryFn: SettingsService.getWhatToExpectOverviewApiV1SettingsWhatToExpectGet,
        staleTime: 60_000,
    });

    const sections = useMemo(
        () => [...(data?.sections ?? [])].sort((left, right) => {
            if (left.sort_order !== right.sort_order) {
                return left.sort_order - right.sort_order;
            }
            return left.id - right.id;
        }),
        [data],
    );

    const invalidateWhatToExpect = () => {
        queryClient.invalidateQueries({queryKey: ['what-to-expect-overview']});
    };

    const showError = (title: string, error: {body?: {detail?: string}; message?: string}) => {
        notifications.show({
            color: 'red',
            title,
            message: error.body?.detail ?? error.message ?? 'Request failed',
        });
    };

    const openSectionModal = (section?: WhatToExpectSectionSummary) => {
        if (!section) {
            openModal({
                modalId: 'whatToExpectSection',
                title: 'Add section',
                size: 'lg',
                children: (
                    <WhatToExpectSectionForm
                        initial={{title: {...EMPTY_LOCALIZED}, text: {...EMPTY_LOCALIZED}, icon: 'info-circle'}}
                        onSubmit={(values) => {
                            SettingsService.addWhatToExpectSectionApiV1SettingsWhatToExpectSectionsPost({
                                requestBody: values,
                            })
                                .then(() => {
                                    invalidateWhatToExpect();
                                    closeModal('whatToExpectSection');
                                })
                                .catch((error) => showError('Could not save section', error));
                        }}
                    />
                ),
            });
            return;
        }

        SettingsService.getWhatToExpectSectionApiV1SettingsWhatToExpectSectionsSectionIdGet({
            sectionId: section.id,
        })
            .then((fullSection) => {
                openModal({
                    modalId: 'whatToExpectSection',
                    title: 'Edit section',
                    size: 'lg',
                    children: (
                        <WhatToExpectSectionForm
                            initial={{
                                title: {...fullSection.title},
                                text: {...fullSection.text},
                                icon: fullSection.icon,
                            }}
                            onSubmit={(values) => {
                                SettingsService.updateWhatToExpectSectionApiV1SettingsWhatToExpectSectionsSectionIdPut({
                                    sectionId: fullSection.id,
                                    requestBody: values,
                                })
                                    .then(() => {
                                        invalidateWhatToExpect();
                                        closeModal('whatToExpectSection');
                                    })
                                    .catch((error) => showError('Could not save section', error));
                            }}
                        />
                    ),
                });
            })
            .catch((error) => showError('Could not load section', error));
    };

    const deleteSection = (section: WhatToExpectSectionSummary) => {
        SettingsService.deleteWhatToExpectSectionApiV1SettingsWhatToExpectSectionsSectionIdDelete({
            sectionId: section.id,
        })
            .then(() => {
                invalidateWhatToExpect();
                closeModal('deleteWhatToExpectSection');
            })
            .catch((error) => showError('Could not delete section', error));
    };

    const reorderSections = (from: number, to: number) => {
        const reordered = [...sections];
        const [moved] = reordered.splice(from, 1);
        reordered.splice(to, 0, moved);
        SettingsService.reorderWhatToExpectSectionsApiV1SettingsWhatToExpectSectionsReorderPut({
            requestBody: {ids: reordered.map((item) => item.id)},
        })
            .then(() => invalidateWhatToExpect())
            .catch((error) => showError('Could not reorder sections', error));
    };

    if (isLoading && !data) {
        return <Text c="dimmed">Loading what to expect…</Text>;
    }

    return (
        <Stack gap="md">
            <Group justify="space-between">
                <Text size="sm" c="dimmed">
                    Drag sections to reorder them on the public page.
                </Text>
                <Button leftSection={<IconPlus size={16}/>} onClick={() => openSectionModal()}>
                    Add section
                </Button>
            </Group>

            {sections.length === 0 ? (
                <Text c="dimmed">No sections yet.</Text>
            ) : (
                <Stack gap="sm">
                    {sections.map((section, index) => (
                        <DraggableSectionRow
                            key={section.id}
                            section={section}
                            index={index}
                            onReorder={reorderSections}
                            onEdit={() => openSectionModal(section)}
                            onDelete={() =>
                                openModal({
                                    modalId: 'deleteWhatToExpectSection',
                                    title: 'Delete section',
                                    children: (
                                        <Stack>
                                            <Text size="sm">
                                                Delete &quot;{sectionPreviewTitle(section)}&quot;?
                                            </Text>
                                            <Group justify="flex-end">
                                                <Button variant="default" onClick={() => closeModal('deleteWhatToExpectSection')}>
                                                    Cancel
                                                </Button>
                                                <Button color="red" onClick={() => deleteSection(section)}>
                                                    Delete
                                                </Button>
                                            </Group>
                                        </Stack>
                                    ),
                                })
                            }
                        />
                    ))}
                </Stack>
            )}
        </Stack>
    );
}

function ArchiveTab() {
    const queryClient = useQueryClient();
    const {data, isLoading} = useQuery({
        queryKey: ['archive-overview'],
        queryFn: SettingsService.getArchiveOverviewApiV1SettingsArchiveGet,
    });

    const years = useMemo(
        () => [...(data?.years ?? [])].sort((left, right) => {
            if (right.year !== left.year) {
                return right.year - left.year;
            }
            return left.id - right.id;
        }),
        [data],
    );
    const [activeYearId, setActiveYearId] = useState<string | null>(null);

    useEffect(() => {
        if (years.length === 0) {
            setActiveYearId(null);
            return;
        }
        if (!activeYearId || !years.some((year) => String(year.id) === activeYearId)) {
            setActiveYearId(String(years[0].id));
        }
    }, [years, activeYearId]);

    const invalidateArchive = () => {
        queryClient.invalidateQueries({queryKey: ['archive-overview']});
    };

    const showError = (title: string, error: {body?: {detail?: string}; message?: string}) => {
        notifications.show({
            color: 'red',
            title,
            message: error.body?.detail ?? error.message ?? 'Request failed',
        });
    };

    const openAddYearModal = () => {
        openModal({
            modalId: 'addArchiveYear',
            title: 'Add archive year',
            children: (
                <YearForm
                    initial={{year: new Date().getFullYear(), subtitle: ''}}
                    onSubmit={(values) => {
                        SettingsService.addArchiveYearApiV1SettingsArchiveYearsPost({
                            requestBody: {
                                year: values.year,
                                subtitle: values.subtitle.trim() || null,
                            },
                        })
                            .then((created) => {
                                invalidateArchive();
                                setActiveYearId(String(created.id));
                                closeModal('addArchiveYear');
                            })
                            .catch((error) => showError('Could not add archive year', error));
                    }}
                />
            ),
        });
    };

    const openEditYearModal = (year: MediaArchiveYear) => {
        openModal({
            modalId: 'editArchiveYear',
            title: 'Edit archive year',
            children: (
                <YearForm
                    initial={{year: year.year, subtitle: year.subtitle ?? ''}}
                    onSubmit={(values) => {
                        SettingsService.updateArchiveYearApiV1SettingsArchiveYearsYearIdPut({
                            yearId: year.id,
                            requestBody: {
                                year: values.year,
                                subtitle: values.subtitle.trim() || null,
                            },
                        })
                            .then(() => {
                                invalidateArchive();
                                closeModal('editArchiveYear');
                            })
                            .catch((error) => showError('Could not update archive year', error));
                    }}
                />
            ),
        });
    };

    const deleteYear = (year: MediaArchiveYear) => {
        SettingsService.deleteArchiveYearApiV1SettingsArchiveYearsYearIdDelete({yearId: year.id})
            .then(() => {
                invalidateArchive();
                closeModal('deleteArchiveYear');
            })
            .catch((error) => showError('Could not delete archive year', error));
    };

    const openLinkModal = (yearId: number, link?: MediaArchiveLink) => {
        const initial: MediaArchiveLinkAdd = link
            ? {title: link.title, url: link.url}
            : {title: '', url: ''};
        openModal({
            modalId: 'archiveLink',
            title: link ? 'Edit archive link' : 'Add archive link',
            children: (
                <LinkForm
                    initial={initial}
                    onSubmit={(values) => {
                        const request = link
                            ? SettingsService.updateArchiveLinkApiV1SettingsArchiveLinksLinkIdPut({
                                linkId: link.id,
                                requestBody: values,
                            })
                            : SettingsService.addArchiveLinkApiV1SettingsArchiveYearsYearIdLinksPost({
                                yearId,
                                requestBody: values,
                            });
                        request
                            .then(() => {
                                invalidateArchive();
                                closeModal('archiveLink');
                            })
                            .catch((error) => showError('Could not save archive link', error));
                    }}
                />
            ),
        });
    };

    const deleteLink = (link: MediaArchiveLink) => {
        SettingsService.deleteArchiveLinkApiV1SettingsArchiveLinksLinkIdDelete({linkId: link.id})
            .then(() => {
                invalidateArchive();
                closeModal('deleteArchiveLink');
            })
            .catch((error) => showError('Could not delete archive link', error));
    };

    const reorderLinks = (year: MediaArchiveYear, from: number, to: number) => {
        const reordered = [...year.links];
        const [moved] = reordered.splice(from, 1);
        reordered.splice(to, 0, moved);
        SettingsService.reorderArchiveLinksApiV1SettingsArchiveYearsYearIdLinksReorderPut({
            yearId: year.id,
            requestBody: {ids: reordered.map((link) => link.id)},
        })
            .then(() => invalidateArchive())
            .catch((error) => showError('Could not reorder archive links', error));
    };

    if (isLoading && !data) {
        return <Text c="dimmed">Loading archive…</Text>;
    }

    return (
        <Stack gap="md">
            <Group justify="space-between">
                <Text size="sm" c="dimmed">
                    Years are sorted automatically. Drag links to reorder them within a year.
                </Text>
                <Button leftSection={<IconPlus size={16}/>} onClick={openAddYearModal}>
                    Add year
                </Button>
            </Group>

            {years.length === 0 ? (
                <Text c="dimmed">No archive years yet.</Text>
            ) : (
                <Tabs value={activeYearId} onChange={setActiveYearId}>
                    <Tabs.List>
                        {years.map((year) => (
                            <Tabs.Tab key={year.id} value={String(year.id)}>
                                {yearTabLabel(year)}
                            </Tabs.Tab>
                        ))}
                    </Tabs.List>

                    {years.map((year) => (
                        <Tabs.Panel key={year.id} value={String(year.id)} pt="md">
                            <Stack gap="md">
                                <Group justify="space-between">
                                    <Group gap="xs">
                                        <Button variant="light" onClick={() => openEditYearModal(year)}>
                                            Edit year
                                        </Button>
                                        <Button
                                            variant="light"
                                            color="red"
                                            onClick={() =>
                                                openModal({
                                                    modalId: 'deleteArchiveYear',
                                                    title: 'Delete archive year',
                                                    children: (
                                                        <Stack>
                                                            <Text size="sm">
                                                                Delete {yearTabLabel(year)} and all links?
                                                            </Text>
                                                            <Group justify="flex-end">
                                                                <Button variant="default" onClick={() => closeModal('deleteArchiveYear')}>
                                                                    Cancel
                                                                </Button>
                                                                <Button color="red" onClick={() => deleteYear(year)}>
                                                                    Delete
                                                                </Button>
                                                            </Group>
                                                        </Stack>
                                                    ),
                                                })
                                            }
                                        >
                                            Delete year
                                        </Button>
                                    </Group>
                                    <Button leftSection={<IconPlus size={16}/>} onClick={() => openLinkModal(year.id)}>
                                        Add link
                                    </Button>
                                </Group>

                                <Stack gap="sm">
                                    {year.links.map((link, index) => (
                                        <DraggableLinkRow
                                            key={link.id}
                                            link={link}
                                            index={index}
                                            onReorder={(from, to) => reorderLinks(year, from, to)}
                                            onEdit={() => openLinkModal(year.id, link)}
                                            onDelete={() =>
                                                openModal({
                                                    modalId: 'deleteArchiveLink',
                                                    title: 'Delete archive link',
                                                    children: (
                                                        <Stack>
                                                            <Text size="sm">Delete &quot;{link.title}&quot;?</Text>
                                                            <Group justify="flex-end">
                                                                <Button variant="default" onClick={() => closeModal('deleteArchiveLink')}>
                                                                    Cancel
                                                                </Button>
                                                                <Button color="red" onClick={() => deleteLink(link)}>
                                                                    Delete
                                                                </Button>
                                                            </Group>
                                                        </Stack>
                                                    ),
                                                })
                                            }
                                        />
                                    ))}
                                    {year.links.length === 0 && (
                                        <Text c="dimmed" size="sm">No links for this year yet.</Text>
                                    )}
                                </Stack>
                            </Stack>
                        </Tabs.Panel>
                    ))}
                </Tabs>
            )}
        </Stack>
    );
}

export const Settings = () => {
    const [activeTab, setActiveTab] = useState<string | null>('settings');

    return (
        <PageLayout
            title="Settings"
            description="Manage public site content, camp dates, media archive links, and what to expect sections."
        >
            <Tabs value={activeTab} onChange={setActiveTab}>
                <Tabs.List>
                    <Tabs.Tab value="settings">Settings</Tabs.Tab>
                    <Tabs.Tab value="archive">Archive</Tabs.Tab>
                    <Tabs.Tab value="what-to-expect">What to expect</Tabs.Tab>
                </Tabs.List>
                <Tabs.Panel value="settings" pt="md">
                    {activeTab === 'settings' && <SettingsForm/>}
                </Tabs.Panel>
                <Tabs.Panel value="archive" pt="md">
                    {activeTab === 'archive' && <ArchiveTab/>}
                </Tabs.Panel>
                <Tabs.Panel value="what-to-expect" pt="md">
                    {activeTab === 'what-to-expect' && <WhatToExpectTab/>}
                </Tabs.Panel>
            </Tabs>
        </PageLayout>
    );
};
